import { __decorate } from "tslib";
import { action, computed, get, isObservableObject, observable, remove, runInAction, set, toJS } from 'mobx';
import raf from 'raf';
import isFunction from 'lodash/isFunction';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import merge from 'lodash/merge';
import defer from 'lodash/defer';
import unionBy from 'lodash/unionBy';
import { getConfig } from '../../../es/configure';
import warning from '../../../es/_util/warning';
import DataSet from './DataSet';
import Validator from '../validator/Validator';
import { DataSetEvents, DataSetSelection, FieldTrim, FieldType } from './enum';
import lookupStore from '../stores/LookupCodeStore';
import lovCodeStore from '../stores/LovCodeStore';
import localeContext from '../locale-context';
import { getBaseType, getLimit, processValue } from './utils';
import isSame from '../_util/isSame';
import PromiseQueue from '../_util/PromiseQueue';
import isSameLike from '../_util/isSameLike';
import { buildURLWithAxiosConfig } from '../axios/utils';
import { getDateFormatByField } from '../field/utils';
import { getLovPara } from '../stores/utils';
import { MAX_SAFE_INTEGER, MIN_SAFE_INTEGER } from '../number-field/utils';
function isEqualDynamicProps(oldProps, newProps) {
    if (newProps === oldProps) {
        return true;
    }
    if (isObject(newProps) && isObject(oldProps) && Object.keys(newProps).length) {
        if (Object.keys(newProps).length !== Object.keys(toJS(oldProps)).length) {
            return false;
        }
        return Object.keys(newProps).every(key => {
            const value = newProps[key];
            const oldValue = oldProps[key];
            if (oldValue === value) {
                return true;
            }
            if (isFunction(value) && isFunction(oldValue)) {
                return value.toString() === oldValue.toString();
            }
            return isEqual(oldValue, value);
        });
    }
    return isEqual(newProps, oldProps);
}
function getPropsFromLovConfig(lovCode, propsName) {
    if (lovCode) {
        const config = lovCodeStore.getConfig(lovCode);
        if (config) {
            if (config[propsName]) {
                return { [propsName]: config[propsName] };
            }
        }
    }
    return {};
}
export default class Field {
    constructor(props = {}, dataSet, record) {
        this.lastDynamicProps = {};
        this.validatorPropKeys = [];
        this.isDynamicPropsComputing = false;
        runInAction(() => {
            this.validator = new Validator(this);
            this.pending = new PromiseQueue();
            this.dataSet = dataSet;
            this.record = record;
            this.dirtyProps = {};
            this.props = props;
            // 优化性能，没有动态属性时不用处理， 直接引用dsField； 有options时，也不处理
            if (!this.getProp('options') && (!record || this.getProp('dynamicProps'))) {
                raf(() => {
                    this.fetchLookup();
                    this.fetchLovConfig();
                });
            }
        });
    }
    get pristineProps() {
        return {
            ...this.props,
            ...this.dirtyProps,
        };
    }
    set pristineProps(props) {
        runInAction(() => {
            const { dirtyProps } = this;
            const dirtyKeys = Object.keys(dirtyProps);
            if (dirtyKeys.length) {
                const newProps = {};
                dirtyKeys.forEach((key) => {
                    const item = this.props[key];
                    newProps[key] = item;
                    if (isSame(item, props[key])) {
                        delete dirtyProps[key];
                    }
                    else {
                        dirtyProps[key] = props[key];
                    }
                });
                this.props = {
                    ...props,
                    ...newProps,
                };
            }
            else {
                this.props = props;
            }
        });
    }
    get lookup() {
        const lookup = this.get('lookup');
        const valueField = this.get('valueField');
        if (lookup) {
            const lookupData = this.get('lookupData') || [];
            return unionBy(lookup.concat(lookupData), valueField);
        }
        return undefined;
    }
    get options() {
        const options = this.get('options');
        if (options) {
            return options;
        }
        // 确保 lookup 相关配置介入观察
        lookupStore.getAxiosConfig(this);
        const optionsProps = this.get('optionsProps');
        const { lookup, type } = this;
        if (lookup) {
            const parentField = this.get('parentField');
            const idField = this.get('idField') || this.get('valueField');
            const selection = this.get('multiple') ? DataSetSelection.multiple : DataSetSelection.single;
            return new DataSet({
                data: lookup,
                paging: false,
                selection,
                idField,
                parentField,
                ...optionsProps,
            });
        }
        const lovCode = this.get('lovCode');
        if (lovCode) {
            if (type === FieldType.object || type === FieldType.auto) {
                return lovCodeStore.getLovDataSet(lovCode, this, optionsProps);
            }
        }
        return undefined;
    }
    get intlFields() {
        const { record, type, name } = this;
        const tlsKey = getConfig('tlsKey');
        if (type === FieldType.intl && record && record.get(tlsKey)) {
            return Object.keys(localeContext.supports).reduce((arr, lang) => {
                const field = record.getField(`${tlsKey}.${name}.${lang}`);
                if (field) {
                    arr.push(field);
                }
                return arr;
            }, []);
        }
        return [];
    }
    get dirty() {
        const { record, name, intlFields } = this;
        if (intlFields.length) {
            return intlFields.some(langField => langField.dirty);
        }
        if (record) {
            const { dirtyData } = record;
            return [...dirtyData.keys()].some(key => key === name || key.startsWith(`${name}.`));
        }
        return false;
    }
    get name() {
        return this.props.name;
    }
    get order() {
        return this.get('order');
    }
    set order(order) {
        this.set('order', order);
    }
    get valid() {
        const { intlFields, validator: { validity: { valid }, }, } = this;
        if (valid && intlFields.length) {
            return intlFields.every(field => field.valid);
        }
        return valid;
    }
    get validationMessage() {
        return this.validator.validationMessage;
    }
    /**
     * 获取所有属性
     * @return 属性对象
     */
    getProps() {
        const dsField = this.findDataSetField();
        const lovCode = this.get('lovCode');
        return merge({ lookupUrl: getConfig('lookupUrl') }, Field.defaultProps, getPropsFromLovConfig(lovCode, 'textField'), getPropsFromLovConfig(lovCode, 'valueField'), dsField && dsField.props, this.props);
    }
    /**
     * 根据属性名获取属性值
     * @param propsName 属性名
     * @return {any}
     */
    get(propsName) {
        const prop = this.getProp(propsName);
        if (prop !== undefined) {
            return prop;
        }
        return Field.defaultProps[propsName];
    }
    getProp(propsName) {
        if (propsName !== 'dynamicProps') {
            const dynamicProps = this.get('dynamicProps');
            if (dynamicProps) {
                if (typeof dynamicProps === 'function') {
                    warning(false, ` The dynamicProps hook will be deprecated. Please use dynamicProps map.
              For e.g,
              Bad case:
              dynamicProps({ record }) {
                return {
                  bind: record.get('xx'),
                  label: record.get('yy'),
                }
              }
              Good case:
              dynamicProps = {
                bind({ record }) {
                  return record.get('xx')
                },
                label({ record }) {
                  return record.get('yy'),
                }
              }`);
                    const props = this.executeDynamicProps(dynamicProps);
                    if (props && propsName in props) {
                        const prop = props[propsName];
                        this.checkDynamicProp(propsName, prop);
                        return prop;
                    }
                }
                else {
                    const dynamicProp = dynamicProps[propsName];
                    if (typeof dynamicProp === 'function') {
                        const prop = this.executeDynamicProps(dynamicProp);
                        if (prop !== undefined) {
                            this.checkDynamicProp(propsName, prop);
                            return prop;
                        }
                    }
                }
                this.checkDynamicProp(propsName, undefined);
            }
        }
        const value = get(this.props, propsName);
        if (value !== undefined) {
            return value;
        }
        const dsField = this.findDataSetField();
        if (dsField) {
            const dsValue = dsField.getProp(propsName);
            if (dsValue !== undefined) {
                return dsValue;
            }
        }
        if (propsName === 'textField' || propsName === 'valueField') {
            const lovCode = this.get('lovCode');
            const lovProps = getPropsFromLovConfig(lovCode, propsName);
            if (propsName in lovProps) {
                return lovProps[propsName];
            }
        }
        if (propsName === 'lookupUrl') {
            return getConfig(propsName);
        }
        if (['min', 'max'].includes(propsName)) {
            if (this.get('type') === FieldType.number) {
                if (propsName === 'max') {
                    return MAX_SAFE_INTEGER;
                }
                else {
                    return MIN_SAFE_INTEGER;
                }
            }
        }
        return undefined;
    }
    /**
     * 设置属性值
     * @param propsName 属性名
     * @param value 属性值
     * @return {any}
     */
    set(propsName, value) {
        const oldValue = this.get(propsName);
        if (!isEqualDynamicProps(oldValue, value)) {
            if (!(propsName in this.dirtyProps)) {
                set(this.dirtyProps, propsName, oldValue);
            }
            else if (isSame(toJS(this.dirtyProps[propsName]), value)) {
                remove(this.dirtyProps, propsName);
            }
            set(this.props, propsName, value);
            const { record, dataSet, name } = this;
            if (record && propsName === 'type') {
                record.set(name, processValue(record.get(name), this));
            }
            if (dataSet) {
                dataSet.fireEvent(DataSetEvents.fieldChange, {
                    dataSet,
                    record,
                    name,
                    field: this,
                    propsName,
                    value,
                    oldValue,
                });
            }
            this.handlePropChange(propsName, value, oldValue);
        }
    }
    /**
     * 根据lookup值获取lookup对象
     * @param value lookup值
     * @return {object}
     */
    getLookupData(value = this.getValue()) {
        const valueField = this.get('valueField');
        const data = {};
        if (this.lookup) {
            return this.lookup.find(obj => isSameLike(get(obj, valueField), value)) || data;
        }
        return data;
    }
    getValue() {
        const { dataSet, name } = this;
        const record = this.record || (dataSet && dataSet.current);
        if (record) {
            return record.get(name);
        }
    }
    /**
     * 可以根据lookup值获取含义
     * @param value lookup值
     * @param boolean showValueIfNotFound
     * @return {string}
     */
    getLookupText(value = this.getValue(), showValueIfNotFound) {
        const textField = this.get('textField');
        const valueField = this.get('valueField');
        const { lookup } = this;
        if (lookup) {
            const found = lookup.find(obj => isSameLike(get(obj, valueField), value));
            if (found) {
                return get(found, textField);
            }
            if (showValueIfNotFound) {
                return value;
            }
            return undefined;
        }
    }
    /**
     * 可以根据options值获取含义
     * @param value opions值
     * @param boolean showValueIfNotFound
     * @return {string}
     */
    getOptionsText(value = this.getValue(), showValueIfNotFound) {
        const textField = this.get('textField');
        const valueField = this.get('valueField');
        const { options } = this;
        if (options) {
            const found = options.find(record => isSameLike(record.get(valueField), value));
            if (found) {
                return found.get(textField);
            }
            if (showValueIfNotFound) {
                return value;
            }
            return undefined;
        }
    }
    /**
     * 根据lookup值获取lookup含义
     * @param value lookup值
     * @param boolean showValueIfNotFound
     * @return {string}
     */
    getText(value = this.getValue(), showValueIfNotFound) {
        const textField = this.get('textField');
        const valueField = this.get('valueField');
        const { lookup, options } = this;
        if (lookup && !isObject(value)) {
            return this.getLookupText(value, showValueIfNotFound);
        }
        if (options) {
            const found = options.find(record => isSameLike(record.get(valueField), value));
            if (found) {
                return found.get(textField);
            }
        }
        if (textField && isObject(value)) {
            if (isObservableObject(value)) {
                return get(value, textField);
            }
            return value[textField];
        }
        return value;
    }
    setOptions(options) {
        this.set('options', options);
    }
    getOptions() {
        return this.options;
    }
    /**
     * 重置设置的属性
     */
    reset() {
        Object.assign(this.props, this.dirtyProps);
        this.dirtyProps = {};
    }
    commit() {
        this.validator.reset();
    }
    /**
     * 是否必选
     * @return true | false
     */
    get required() {
        return this.get('required');
    }
    /**
     * 设置是否必选
     * @param required 是否必选
     */
    set required(required) {
        this.set('required', required);
    }
    /**
     * 是否只读
     * @return true | false
     */
    get readOnly() {
        return this.get('readOnly');
    }
    /**
     * 是否禁用
     * @return true | false
     */
    get disabled() {
        return this.get('disabled');
    }
    /**
     * 设置是否只读
     * @param readOnly 是否只读
     */
    set readOnly(readOnly) {
        this.set('readOnly', readOnly);
    }
    /**
     * 设置是否禁用
     * @param disabled 是否禁用
     */
    set disabled(disabled) {
        this.set('disabled', disabled);
    }
    /**
     * 获取字段类型
     * @return 获取字段类型
     */
    get type() {
        return this.get('type');
    }
    /**
     * 设置字段类型
     * @param type 字段类型
     */
    set type(type) {
        this.set('type', type);
    }
    /**
     * 设置Lov的查询参数
     * @param {String} name
     * @param {Object} value
     */
    setLovPara(name, value) {
        const p = toJS(this.get('lovPara')) || {};
        if (value === null) {
            delete p[name];
        }
        else {
            p[name] = value;
        }
        this.set('lovPara', p);
    }
    getValidatorProps() {
        const { record, dataSet, name, type, required } = this;
        if (record) {
            const baseType = getBaseType(type);
            const customValidator = this.get('validator');
            const max = this.get('max');
            const min = this.get('min');
            const format = this.get('format') || getDateFormatByField(this, this.type);
            const pattern = this.get('pattern');
            const step = this.get('step');
            const nonStrictStep = this.get('nonStrictStep') === undefined ? getConfig('numberFieldNonStrictStep') : this.get('nonStrictStep');
            const minLength = baseType !== FieldType.string ? undefined : this.get('minLength');
            const maxLength = baseType !== FieldType.string ? undefined : this.get('maxLength');
            const label = this.get('label');
            const range = this.get('range');
            const multiple = this.get('multiple');
            const unique = this.get('unique');
            const defaultValidationMessages = this.get('defaultValidationMessages');
            const validatorProps = {
                type,
                required,
                record,
                dataSet,
                name,
                unique,
                customValidator,
                pattern,
                max: getLimit(max, record),
                min: getLimit(min, record),
                step,
                nonStrictStep,
                minLength,
                maxLength,
                label,
                range,
                multiple,
                format,
                defaultValidationMessages,
            };
            if (!this.validatorPropKeys.length) {
                this.validatorPropKeys = Object.keys(validatorProps);
            }
            return validatorProps;
        }
    }
    /**
     * 校验字段值
     * 只有通过record.getField()获取的field才能校验
     * @return true | false
     */
    async checkValidity() {
        let valid = true;
        const { record, validator, name } = this;
        if (record) {
            validator.reset();
            const value = record.get(name);
            valid = await validator.checkValidity(value);
        }
        return valid;
    }
    /**
     * 请求lookup值, 如有缓存值直接获得。
     * @param noCache default: undefined
     * @return Promise<object[]>
     */
    async fetchLookup(noCache = undefined) {
        const batch = this.get('lookupBatchAxiosConfig') || getConfig('lookupBatchAxiosConfig');
        const lookupCode = this.get('lookupCode');
        const lovPara = getLovPara(this, this.record);
        const dsField = this.findDataSetField();
        let result;
        if (batch && lookupCode && Object.keys(lovPara).length === 0) {
            if (dsField && dsField.get('lookupCode') === lookupCode) {
                this.set('lookup', undefined);
                return dsField.get('lookup');
            }
            result = await this.pending.add(lookupStore.fetchLookupDataInBatch(lookupCode, batch));
        }
        else {
            const axiosConfig = lookupStore.getAxiosConfig(this, noCache);
            if (dsField && noCache === false) {
                const dsConfig = lookupStore.getAxiosConfig(dsField);
                if (dsConfig.url &&
                    buildURLWithAxiosConfig(dsConfig) === buildURLWithAxiosConfig(axiosConfig)) {
                    this.set('lookup', undefined);
                    return dsField.get('lookup');
                }
            }
            if (axiosConfig.url) {
                result = await this.pending.add(lookupStore.fetchLookupData(axiosConfig));
            }
        }
        if (result) {
            runInAction(() => {
                const { lookup } = this;
                this.set('lookup', result);
                const value = this.getValue();
                const valueField = this.get('valueField');
                if (value && valueField && lookup) {
                    const values = this.get('multiple') ? [].concat(...value) : [].concat(value);
                    this.set('lookupData', values.reduce((lookupData, v) => {
                        const found = lookup.find(item => isSameLike(item[valueField], v));
                        if (found) {
                            lookupData.push(found);
                        }
                        return lookupData;
                    }, []));
                }
            });
        }
        return result;
    }
    fetchLovConfig() {
        const lovCode = this.get('lovCode');
        if (lovCode) {
            this.pending.add(lovCodeStore.fetchConfig(lovCode, this));
        }
    }
    isValid() {
        return this.valid;
    }
    getValidationMessage() {
        return this.validator.validationMessage;
    }
    getValidityState() {
        return this.validator.validity;
    }
    getValidationErrorValues() {
        return this.validator.validationResults;
    }
    ready() {
        // const { options } = this;
        // return Promise.all([this.pending.ready(), options && options.ready()]);
        return this.pending.ready();
    }
    findDataSetField() {
        const { dataSet, name, record } = this;
        if (record && dataSet && name) {
            return dataSet.getField(name);
        }
    }
    checkDynamicProp(propsName, newProp) {
        const oldProp = this.lastDynamicProps[propsName];
        if (!isEqualDynamicProps(oldProp, newProp)) {
            defer(action(() => {
                if (this.validatorPropKeys.includes(propsName) || propsName === 'validator') {
                    this.validator.reset();
                }
                this.handlePropChange(propsName, newProp, oldProp);
            }));
        }
        this.lastDynamicProps[propsName] = newProp;
    }
    handlePropChange(propsName, newProp, oldProp) {
        if (propsName === 'bind' && this.type !== FieldType.intl) {
            const { record } = this;
            if (record && !this.dirty) {
                if (newProp && oldProp) {
                    record.init(newProp, record.get(oldProp));
                }
                if (oldProp) {
                    record.init(oldProp, undefined);
                }
            }
            return;
        }
        if ([
            'type',
            'lookupUrl',
            'lookupCode',
            'lookupAxiosConfig',
            'lovCode',
            'lovQueryAxiosConfig',
            'lovPara',
            'cascadeMap',
            'lovQueryUrl',
            'optionsProps',
        ].includes(propsName)) {
            this.set('lookupData', undefined);
            this.fetchLookup();
        }
        if (['lovCode', 'lovDefineAxiosConfig', 'lovDefineUrl', 'optionsProps'].includes(propsName)) {
            this.fetchLovConfig();
        }
    }
    executeDynamicProps(dynamicProps) {
        const { dataSet, name, record } = this;
        if (this.isDynamicPropsComputing) {
            warning(false, `Cycle dynamicProps execution of field<${name}>.`);
        }
        else if (dataSet && record) {
            this.isDynamicPropsComputing = true;
            const props = dynamicProps({ dataSet, record, name });
            this.isDynamicPropsComputing = false;
            return props;
        }
    }
}
Field.defaultProps = {
    type: FieldType.auto,
    required: false,
    readOnly: false,
    disabled: false,
    group: false,
    textField: 'meaning',
    valueField: 'value',
    trueValue: true,
    falseValue: false,
    trim: FieldTrim.both,
};
__decorate([
    observable
], Field.prototype, "props", void 0);
__decorate([
    observable
], Field.prototype, "dirtyProps", void 0);
__decorate([
    computed
], Field.prototype, "pristineProps", null);
__decorate([
    computed
], Field.prototype, "lookup", null);
__decorate([
    computed
], Field.prototype, "options", null);
__decorate([
    computed
], Field.prototype, "intlFields", null);
__decorate([
    computed
], Field.prototype, "dirty", null);
__decorate([
    computed
], Field.prototype, "valid", null);
__decorate([
    computed
], Field.prototype, "validationMessage", null);
__decorate([
    action
], Field.prototype, "set", null);
__decorate([
    action
], Field.prototype, "reset", null);
__decorate([
    action
], Field.prototype, "commit", null);
__decorate([
    action
], Field.prototype, "setLovPara", null);
__decorate([
    action
], Field.prototype, "checkValidity", null);
